"use client";
import { useFormContext } from "react-hook-form";
import { Label } from "~/components/v3/shadcn/Label";
import { Input } from "~/components/v3/themed/Input";

import { RSVPStepsFormInputData } from "../validators";
import { useEffect } from "react";
import { If } from "~/components/v3/Conditional";
import { LuMail, LuPhone } from "react-icons/lu";
import CountryCodeSelector from "~/components/themed/CountryCodeSelector";
import { BASE_INPUT_STYLES } from "~/components/v3/themed/BASE_STYLE_DEFINITIONS";
import { cn } from "~/utils/fontsConfig";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { useWhiteLabel } from "~/app/providers";

const QuickRegisterModalContent = ({
    setNextDisabled,
    customization,
}: {
    setNextDisabled: (disabled: boolean) => void;
    customization?: CustomizableLayout;
}) => {
    const whiteProps = useWhiteLabel()?.whiteLabelProps;
    const {
        register,
        formState: { errors, isValid, dirtyFields },
        trigger,
        setValue,
        watch,
        clearErrors,
    } = useFormContext<RSVPStepsFormInputData>();

    const verificationMethod = watch("register.contactMethod");

    const isEmailRequired =
        verificationMethod === "email" || verificationMethod === undefined;

    const isPhoneRequired = verificationMethod === "sms";

    useEffect(() => {
        setNextDisabled(!isValid);
    }, [isValid, setNextDisabled]);

    const handleChangeVerificationMethod = (method: "email" | "sms") => {
        setValue("register.contactMethod", method);
        // reset the error for the other method
        if (method === "email") {
            clearErrors("register.email");
        } else {
            clearErrors("register.phone");
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div>
                <Label htmlFor="name" className={customization?.labelClassName}>
                    Name
                </Label>
                <Input
                    {...register("register.name", {
                        required: "Name is required",
                    })}
                    placeholder="Your name"
                    onBlur={() => trigger("register.name")}
                    className={cn(customization?.inputClassName)}
                />
                {errors.register?.name && (
                    <p className="text-sm text-red-500">
                        {errors.register.name.message}
                    </p>
                )}
            </div>
            <div className="flex flex-col gap-2">
                <div className="flex select-none items-center justify-between gap-2">
                    <If condition={isEmailRequired}>
                        <Label
                            htmlFor="email"
                            className={cn(
                                customization?.labelClassName,
                                "flex flex-col gap-1",
                            )}
                        >
                            Email
                            {whiteProps?.subscriptionUrl && (
                                <span className="text-[12px] text-black/50">
                                    (Use an email address associated with your{" "}
                                    {whiteProps?.name} subscrition)
                                </span>
                            )}
                        </Label>

                        <button
                            className={cn(
                                "flex items-center gap-2 font-[500] text-v3-primary hover:text-opacity-60",
                                customization?.linkClassName,
                                whiteProps?.hostingLink && "hidden",
                            )}
                            onClick={(e) => {
                                e.preventDefault();
                                handleChangeVerificationMethod("sms");
                            }}
                        >
                            <LuPhone className="h-4 w-4" />
                            Use phone instead{" "}
                        </button>
                    </If>
                    <If condition={isPhoneRequired}>
                        <Label
                            htmlFor="phone"
                            className={customization?.labelClassName}
                        >
                            Phone
                        </Label>

                        <button
                            className={cn(
                                "flex items-center gap-2 font-[500] text-v3-primary hover:text-opacity-60",
                                customization?.linkClassName,
                            )}
                            onClick={(e) => {
                                e.preventDefault();
                                handleChangeVerificationMethod("email");
                            }}
                        >
                            <LuMail className="h-4 w-4" />
                            Use email instead
                        </button>
                    </If>
                </div>
                <If condition={isEmailRequired}>
                    <Input
                        {...register("register.email", {
                            required: isEmailRequired
                                ? "Email is required"
                                : undefined,
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: "Invalid email address",
                            },
                        })}
                        placeholder="Your email"
                        onBlur={() => trigger("register.email")}
                        className={cn(customization?.inputClassName)}
                    />
                </If>
                <If condition={isPhoneRequired}>
                    <div className="flex w-full items-center gap-2">
                        <CountryCodeSelector
                            countryCode={watch("register.countryCode")}
                            setCountryCode={(value) => {
                                setValue("register.countryCode", value);
                            }}
                            className={cn(
                                ...BASE_INPUT_STYLES,
                                "border-1 w-32 rounded-md border-black/10 ",
                                customization?.inputClassName,
                            )}
                        />
                        <Input
                            className={cn(customization?.inputClassName)}
                            {...register("register.phone", {
                                required: isPhoneRequired
                                    ? "Phone number is required"
                                    : undefined,
                                pattern: {
                                    value: /^\d{4,15}$/,
                                    message: "Invalid phone number",
                                },
                            })}
                            onChange={(e) => {
                                const value = e.target.value;
                                const cleanedValue = value.replace(/\D/g, "");
                                setValue("register.phone", cleanedValue, {
                                    shouldValidate: true,
                                });
                            }}
                            placeholder="Your phone number"
                            onBlur={() => trigger("register.phone")}
                        />
                    </div>
                </If>
                {errors.register?.email && (
                    <p className="text-sm text-red-500">
                        {errors.register.email.message}
                    </p>
                )}
            </div>
        </div>
    );
};

export default QuickRegisterModalContent;
